/**
 * Creator Partner portal — client-side renderer.
 *
 * Fetches the signed App Proxy payload from /apps/partner/api/me, picks the
 * right view (stage card vs full dashboard) and wires up the care-package
 * picker. The payload contract is defined in pemmican-emails:
 * backend/routes/app_proxy.py — keep the field names in sync if it changes.
 */

(function () {
  const PROXY_BASE = '/apps/partner';

  // Admin preview: `?as=<affiliate email or id>` is forwarded to the proxy,
  // which only honours it for allowlisted admins. Sent on the care-package
  // POST too, where the backend refuses it — so a preview can never write.
  const VIEW_AS = new URLSearchParams(location.search).get('as') || '';

  function proxyUrl(path) {
    if (!VIEW_AS) return PROXY_BASE + path;
    const sep = path.indexOf('?') === -1 ? '?' : '&';
    return PROXY_BASE + path + sep + 'as=' + encodeURIComponent(VIEW_AS);
  }

  const root = document.querySelector('[data-pp-portal]');
  if (!root) return;

  const els = {
    loading: root.querySelector('[data-pp-loading]'),
    signedOut: root.querySelector('[data-pp-signedout]'),
    notEnrolled: root.querySelector('[data-pp-notenrolled]'),
    error: root.querySelector('[data-pp-error]'),
    stage: root.querySelector('[data-pp-stage]'),
    dashboard: root.querySelector('[data-pp-dashboard]'),
  };

  function show(el) {
    Object.values(els).forEach((node) => node && node.classList.add('pp-hidden'));
    if (el) el.classList.remove('pp-hidden');
  }

  function fmtMoney(amount, currency) {
    const cur = currency || 'GBP';
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: cur }).format(
        Number(amount || 0),
      );
    } catch (e) {
      return cur + ' ' + Number(amount || 0).toFixed(2);
    }
  }

  // Where the order came from, and the rate it earned. Rows written before
  // source tracking have no `source` — those are organic.
  function sourcePill(c) {
    const paid = c.source === 'paid_social';
    const rate = Number(c.rate) > 0 ? Math.round(Number(c.rate) * 100) : paid ? 5 : 30;
    return (
      '<span class="pp-pill pp-pill--' +
      (paid ? 'paidsocial' : 'organic') +
      '">' +
      (paid ? 'Paid social' : 'Organic') +
      ' · ' +
      rate +
      '%</span>'
    );
  }

  function fmtDate(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  function setText(selector, value) {
    const node = root.querySelector(selector);
    if (node) node.textContent = value;
  }

  function setViewingAsBanner(label) {
    const banner = root.querySelector('[data-pp-viewing-as]');
    if (!banner) return;
    banner.textContent = 'Admin preview — ' + label + '. Read-only.';
    banner.classList.remove('pp-hidden');
  }

  async function load() {
    show(els.loading);
    if (VIEW_AS) setViewingAsBanner('viewing the portal of ' + VIEW_AS);
    let res;
    try {
      res = await fetch(proxyUrl('/api/me'), { credentials: 'same-origin' });
    } catch (e) {
      show(els.error);
      return;
    }
    if (res.status === 401) {
      show(els.signedOut);
      return;
    }
    if (res.status === 404) {
      show(els.notEnrolled);
      return;
    }
    if (!res.ok) {
      show(els.error);
      return;
    }
    const data = await res.json();
    if (data && data.viewing_as) {
      setViewingAsBanner("this is " + (data.name || data.email || VIEW_AS) + "'s portal");
    }
    if (data && data.stage) {
      renderStage(data);
    } else {
      renderDashboard(data);
    }
  }

  function renderStage(data) {
    setText('[data-pp-stage-headline]', data.stage.headline || '');
    setText('[data-pp-stage-body]', data.stage.body || '');
    const meta = root.querySelector('[data-pp-stage-meta]');
    if (meta) {
      if (data.stage.trial_pack_sent_at) {
        meta.textContent = 'Trial pack shipped on ' + fmtDate(data.stage.trial_pack_sent_at) + '.';
        meta.classList.remove('pp-hidden');
      } else {
        meta.classList.add('pp-hidden');
      }
    }
    setText('[data-pp-name]', data.name || '');
    setText('[data-pp-email]', data.email || '');

    // Show the discount code on the stage card too — evaluating creators
    // get a code as soon as they're approved and they can start using it
    // before the admin promotes them to Active. Pending applicants don't
    // have one yet, so we hide the block in that case.
    const codeBlock = root.querySelector('[data-pp-stage-code]');
    if (codeBlock) {
      if (data.discount_code) {
        codeBlock.classList.remove('pp-hidden');
        setText('[data-pp-stage-code-value]', data.discount_code);
        const copyBtn = codeBlock.querySelector('[data-pp-stage-code-copy]');
        if (copyBtn) {
          copyBtn.onclick = () => {
            navigator.clipboard.writeText(data.discount_code).then(() => {
              copyBtn.textContent = 'Copied';
              copyBtn.dataset.copied = 'true';
              setTimeout(() => {
                copyBtn.textContent = 'Copy';
                copyBtn.dataset.copied = 'false';
              }, 1500);
            });
          };
        }
      } else {
        codeBlock.classList.add('pp-hidden');
      }
    }

    show(els.stage);
  }

  function renderDashboard(data) {
    setText('[data-pp-name]', data.name || '');
    setText('[data-pp-email]', data.email || '');

    const codeWrap = root.querySelector('[data-pp-code]');
    if (codeWrap) {
      if (data.discount_code) {
        codeWrap.classList.remove('pp-hidden');
        setText('[data-pp-code-value]', data.discount_code);
        const copyBtn = codeWrap.querySelector('[data-pp-code-copy]');
        if (copyBtn) {
          copyBtn.onclick = () => {
            navigator.clipboard.writeText(data.discount_code).then(() => {
              copyBtn.textContent = 'Copied';
              copyBtn.dataset.copied = 'true';
              setTimeout(() => {
                copyBtn.textContent = 'Copy';
                copyBtn.dataset.copied = 'false';
              }, 1500);
            });
          };
        }
      } else {
        codeWrap.classList.add('pp-hidden');
      }
    }

    // Big numbers
    const counts = data.purchase_counts || {};
    const totals = data.totals || {};
    setText('[data-pp-next-payout]', data.next_payout_date || '—');
    setText('[data-pp-mtd-earnings]', fmtMoney(totals.month_to_date_unpaid, data.currency));
    setText('[data-pp-mtd-purchases]', String(counts.month_to_date || 0));
    setText('[data-pp-lifetime-earnings]', fmtMoney(totals.paid, data.currency));
    setText(
      '[data-pp-lifetime-earnings-sub]',
      fmtMoney(totals.pending, data.currency) + ' pending',
    );
    setText('[data-pp-lifetime-purchases]', String(counts.lifetime || 0));
    setText(
      '[data-pp-lifetime-purchases-sub]',
      fmtMoney(counts.revenue_lifetime, data.currency) + ' net sales driven',
    );

    // Recent activity
    const tbody = root.querySelector('[data-pp-activity-body]');
    const empty = root.querySelector('[data-pp-activity-empty]');
    const table = root.querySelector('[data-pp-activity-table]');
    if (tbody) tbody.innerHTML = '';
    const rows = data.recent_commissions || [];
    if (!rows.length) {
      if (empty) empty.classList.remove('pp-hidden');
      if (table) table.classList.add('pp-hidden');
    } else {
      if (empty) empty.classList.add('pp-hidden');
      if (table) table.classList.remove('pp-hidden');
      rows.forEach((c) => {
        const tr = document.createElement('tr');
        const statusPill =
          c.status === 'paid'
            ? '<span class="pp-pill pp-pill--paid">Paid</span>'
            : '<span class="pp-pill pp-pill--pending">Pending</span>';
        tr.innerHTML =
          '<td>' + fmtDate(c.created_at) + '</td>' +
          '<td>' + (c.order_name || '—') + '</td>' +
          '<td class="num">' + fmtMoney(c.order_total, c.currency) + '</td>' +
          '<td>' + sourcePill(c) + '</td>' +
          '<td class="num">' + fmtMoney(c.amount, c.currency) + '</td>' +
          '<td>' + statusPill + '</td>';
        tbody.appendChild(tr);
      });
    }

    // Payouts
    const payoutsList = root.querySelector('[data-pp-payouts-list]');
    const payoutsBlock = root.querySelector('[data-pp-payouts]');
    const payouts = data.payouts || [];
    if (payoutsList) payoutsList.innerHTML = '';
    if (!payouts.length) {
      if (payoutsBlock) payoutsBlock.classList.add('pp-hidden');
    } else {
      if (payoutsBlock) payoutsBlock.classList.remove('pp-hidden');
      payouts.forEach((p) => {
        const li = document.createElement('li');
        const status =
          p.status === 'paid'
            ? '<span class="pp-pill pp-pill--paid">Paid</span>'
            : '<span class="pp-pill pp-pill--pending">Pending</span>';
        li.innerHTML =
          '<span>' + fmtDate(p.period_start) + ' – ' + fmtDate(p.period_end) + '</span>' +
          '<span class="num">' + fmtMoney(p.total_amount, p.currency) + '</span>' +
          '<span>' + status + '</span>';
        payoutsList.appendChild(li);
      });
    }

    // Care package picker (Creator Partners only)
    const creatorBlock = root.querySelector('[data-pp-creator]');
    if (data.creator) {
      if (creatorBlock) creatorBlock.classList.remove('pp-hidden');
      wireCarePackagePicker(data);
    } else if (creatorBlock) {
      creatorBlock.classList.add('pp-hidden');
    }

    show(els.dashboard);
  }

  async function wireCarePackagePicker(data) {
    const select = root.querySelector('[data-pp-package-select]');
    const cta = root.querySelector('[data-pp-package-save]');
    const current = root.querySelector('[data-pp-package-current]');
    const cadence = root.querySelector('[data-pp-package-cadence]');
    const msg = root.querySelector('[data-pp-package-msg]');
    if (!select || !cta) return;

    if (VIEW_AS) {
      cta.disabled = true;
      cta.title = 'Read-only in admin preview';
    }

    const selectedId = data.creator?.care_package_option_id || '';
    const currentName = data.creator?.care_package_option?.name || '—';
    if (current) current.innerHTML = 'Current pick: <strong>' + currentName + '</strong>';

    if (cadence) {
      const lastIso = data.creator?.care_package_last_sent_at;
      const nextIso = data.creator?.care_package_next_due_at;
      const dueNow = data.creator?.care_package_due_now === true;
      if (!lastIso) {
        if (selectedId) {
          cadence.textContent = 'Your first shipment will go out within a day of your selection.';
        } else {
          cadence.textContent = 'Pick a package to start your monthly shipments.';
        }
      } else if (dueNow) {
        cadence.textContent = 'Last shipment ' + fmtDate(lastIso) + ' · the next one is due now.';
      } else {
        cadence.textContent = 'Last shipment ' + fmtDate(lastIso) + ' · next on ' + fmtDate(nextIso) + '.';
      }
    }

    // Fetch options lazily
    let optionsCache = null;
    try {
      const res = await fetch(proxyUrl('/api/care-package-options'), {
        credentials: 'same-origin',
      });
      if (res.ok) optionsCache = (await res.json()).options || [];
    } catch (e) {
      // Picker simply stays disabled; CTA is informational below.
    }

    if (!optionsCache || !optionsCache.length) {
      select.innerHTML = '<option value="">No options available</option>';
      cta.disabled = true;
      return;
    }
    select.innerHTML = '<option value="">— pick one —</option>';
    optionsCache.forEach((o) => {
      const opt = document.createElement('option');
      opt.value = o.id;
      opt.textContent = o.name;
      if (o.id === selectedId) opt.selected = true;
      select.appendChild(opt);
    });

    cta.onclick = async () => {
      const optionId = select.value;
      if (!optionId) return;
      cta.disabled = true;
      cta.textContent = 'Saving…';
      if (msg) {
        msg.textContent = '';
        msg.classList.remove('pp-picker__msg--ok', 'pp-picker__msg--err');
      }
      try {
        const res = await fetch(proxyUrl('/api/care-package'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ option_id: optionId }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || 'Could not save your pick.');
        }
        const newName =
          (optionsCache.find((o) => o.id === optionId) || {}).name || '—';
        if (current) current.innerHTML = 'Current pick: <strong>' + newName + '</strong>';
        if (msg) {
          msg.textContent = 'Saved.';
          msg.classList.add('pp-picker__msg--ok');
        }
      } catch (e) {
        if (msg) {
          msg.textContent = e.message || 'Could not save your pick.';
          msg.classList.add('pp-picker__msg--err');
        }
      } finally {
        cta.disabled = Boolean(VIEW_AS);
        cta.textContent = 'Save selection';
      }
    };
  }

  load();
})();
